<template>
  <q-expansion-item
    ref="expansionItem"
    class="tm-expansion-item"
    :model-value="modelValue"
    :expand-separator="expandSeparator"
    :expand-icon="expandIcon"
    :hide-expand-icon="hideExpandIcon"
    :icon="icon"
    :disable="disable"
    :duration="duration"
    :switch-toggle-side="switchToggleSide"
    :toggle-aria-label="toggleAriaLabel"
    :header-style="headerStyle"
    @update:model-value="$emit('update:model-value', $event)"
    @before-show="$emit('before-show', $event)"
  >
    <template v-slot:header>
      <slot name="header" />
    </template>
    <slot />
  </q-expansion-item>
</template>

<script setup lang="ts">
import type { QExpansionItem, VueStyleProp } from 'quasar'
import { ref } from '@/composition/vue/compositionApi'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean | null
    expandSeparator?: boolean | undefined
    expandIcon?: string | undefined
    hideExpandIcon?: boolean | undefined
    icon?: string | undefined
    disable?: boolean | undefined
    duration?: number | undefined
    switchToggleSide?: boolean | undefined
    toggleAriaLabel?: string | undefined
    headerStyle?: VueStyleProp | undefined
  }>(),
  {
    modelValue: null,
  },
)

defineEmits<{
  'update:model-value': [value: boolean]
  'before-show': [evt: Event]
}>()

const expansionItem = ref<QExpansionItem>()

const show = () => {
  expansionItem.value?.show()
}

defineExpose({
  show,
})
</script>

<style lang="scss" scoped>
.tm-expansion-item {
  :deep(.q-item) {
    align-items: var(--tm-expansion-item-align-items, normal);
    > .q-focus-helper {
      opacity: 0;
      visibility: hidden;
    }

    &:focus-visible {
      outline: 2px solid $blue400;
      outline-offset: -2px;
    }
  }

  :deep(.q-item__section--side) {
    margin-top: var(--tm-expansion-item-section--side-margin-top, 0);
    padding-right: var(--tm-expansion-item-section--side-padding-right, 0);
  }

  :deep(.q-item__section--avatar) {
    min-width: var(--tm-expansion-item-section--avatar-min-width, 56px);
  }

  :deep(.q-expansion-item__toggle-icon) {
    color: var(--tm-expansion-item-toggle-icon-color, inherit);
    font-size: var(--tm-expansion-item-toggle-icon-font-size, 24px);
    background: var(--tm-expansion-item-toggle-icon-background, initial);
    border-radius: var(--tm-expansion-item-toggle-border-radius, 0);
    width: var(--tm-expansion-item-toggle-width, 1em);
    height: var(--tm-expansion-item-toggle-height, 1em);
  }

  :deep(.q-item.disabled) {
    opacity: 1 !important;
  }
}
</style>
